import React from 'react';
import { Box, Container, Grid, Typography, Link, IconButton, Divider, Stack } from '@mui/material';
import FacebookIcon       from '@mui/icons-material/Facebook';
import InstagramIcon      from '@mui/icons-material/Instagram';
import EmailIcon          from '@mui/icons-material/Email';
import PhoneIcon          from '@mui/icons-material/Phone';
import LocationOnIcon     from '@mui/icons-material/LocationOn';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import DirectionsBusIcon  from '@mui/icons-material/DirectionsBus';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

const TERRACOTTA = '#C4622D';
const GOLD       = '#C9973A';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';

export default function Footer() {
    const { t } = useTranslation();
    const year  = new Date().getFullYear();

    return (
        <Box component="footer" sx={{
            background: `linear-gradient(160deg, ${WALNUT} 0%, #4A2810 55%, #6B3A20 100%)`,
            color: 'white', mt: 'auto', position: 'relative', overflow: 'hidden',
            '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
                background: `linear-gradient(90deg, ${TERRACOTTA}, ${GOLD}, ${FOREST}, ${GOLD}, ${TERRACOTTA})` },
            '&::after':  { content: '""', position: 'absolute', bottom: 0, right: 0,
                width: '400px', height: '400px', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(201,151,58,0.07) 0%, transparent 70%)',
                pointerEvents: 'none' },
        }}>
            <Container maxWidth="lg" sx={{ pt: 7, pb: 4, position: 'relative', zIndex: 1 }}>
                <Grid container spacing={5}>

                    {/* Brand */}
                    <Grid item xs={12} md={4}>
                        <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-1px', mb: 0.5, lineHeight: 1 }}>
                            HAY<span style={{ color: GOLD }}>TOUR</span>
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.55, display: 'block', mb: 2, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                            {t('footer.tagline')}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.75, lineHeight: 1.9, mb: 3, maxWidth: 300 }}>
                            {t('footer.brand_text')}
                        </Typography>
                        <Stack spacing={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <EmailIcon sx={{ fontSize: '0.9rem', color: GOLD }} />
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>garnikmikayelyan.dev@gmail.com</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <PhoneIcon sx={{ fontSize: '0.9rem', color: GOLD }} />
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>+34 644 036 977</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <LocationOnIcon sx={{ fontSize: '0.9rem', color: GOLD }} />
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>{t('footer.address')}</Typography>
                            </Box>
                        </Stack>
                    </Grid>

                    {/* Services */}
                    <Grid item xs={6} md={2.5}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2.5, color: GOLD, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.75rem' }}>
                            Our Services
                        </Typography>
                        <Stack spacing={1.2}>
                            <FooterLink to="/bike-travel-offers" icon={<DirectionsBikeIcon sx={{ fontSize: '0.9rem' }} />}>Bike Travellings</FooterLink>
                            <FooterLink to="/tours"             icon={<DirectionsWalkIcon sx={{ fontSize: '0.9rem' }} />}>Guided Tours</FooterLink>
                            <FooterLink to="/rentals"           icon={<DirectionsBusIcon  sx={{ fontSize: '0.9rem' }} />}>Rentals</FooterLink>
                        </Stack>
                    </Grid>

                    {/* Company */}
                    <Grid item xs={6} md={2}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2.5, color: GOLD, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.75rem' }}>
                            {t('footer.company')}
                        </Typography>
                        <Stack spacing={1.2}>
                            <FooterLink to="/about">About Us</FooterLink>
                            <FooterLink to="/contact">Contact</FooterLink>
                            <FooterLink to="/privacy">Privacy Policy</FooterLink>
                            <FooterLink to="/terms">Terms of Service</FooterLink>
                            <FooterLink to="/faq">FAQ</FooterLink>
                        </Stack>
                    </Grid>

                    {/* Social + Proverb */}
                    <Grid item xs={12} md={3.5}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2.5, color: GOLD, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.75rem' }}>
                            {t('footer.follow')}
                        </Typography>
                        <Stack direction="row" spacing={1} mb={3}>
                            <SocialButton href="https://facebook.com"  label="Facebook"><FacebookIcon  fontSize="small" /></SocialButton>
                            <SocialButton href="https://instagram.com" label="Instagram"><InstagramIcon fontSize="small" /></SocialButton>
                        </Stack>
                        <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(201,151,58,0.22)' }}>
                            <Typography variant="caption" sx={{ opacity: 0.5, display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                {t('footer.proverb_title')}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.85, fontStyle: 'italic', lineHeight: 1.7 }}>
                                {t('footer.proverb')}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ mt: 5, mb: 3, borderColor: 'rgba(255,255,255,0.08)' }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Typography variant="caption" sx={{ opacity: 0.4 }}>{t('footer.rights', { year })}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.4 }}>{t('footer.made_in')}</Typography>
                </Box>
            </Container>
        </Box>
    );
}

function FooterLink({ to, children, icon }) {
    return (
        <Link component={RouterLink} to={to} color="inherit" underline="none" display="flex" alignItems="center" gap={0.8}
              sx={{ opacity: 0.7, fontSize: '0.875rem', transition: 'all 0.2s ease',
                  '&:hover': { opacity: 1, color: GOLD, transform: 'translateX(4px)' } }}>
            {icon && <Box sx={{ opacity: 0.6, display: 'flex' }}>{icon}</Box>}
            {children}
        </Link>
    );
}

function SocialButton({ href, label, children }) {
    return (
        <IconButton component="a" href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                    sx={{ color: 'white', width: 40, height: 40, border: '1px solid rgba(255,255,255,0.15)', borderRadius: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': { bgcolor: TERRACOTTA, borderColor: TERRACOTTA, transform: 'translateY(-3px)',
                            boxShadow: '0 6px 16px rgba(196,98,45,0.4)' } }}>
            {children}
        </IconButton>
    );
}